
import React from 'react';
import { Button } from '@/components/ui/button';
import { NavigateFunction } from 'react-router-dom';
import { Card, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { ResponseActions } from '../ResponseActions';

export const renderAtheismDefenseContent = (item: any, navigate: NavigateFunction) => {
  if (!item || !item.content) return <p>No content available</p>;

  // Helper function to check if a text field has meaningful content
  const hasContent = (text: string) => {
    return !!text && 
           text.length > 10 && 
           !text.includes("No detailed") &&
           !text.includes("not provided") &&
           !text.toLowerCase().startsWith("no ");
  };

  // Helper function to deduplicate explanation text by removing repeated paragraphs
  const deduplicateText = (text: string) => {
    if (!text) return '';
    
    const lines = text.split(/\n+/);
    const uniqueLines = new Set();
    
    return lines.filter(line => {
      const trimmed = line.trim();
      if (!trimmed || uniqueLines.has(trimmed)) return false;
      uniqueLines.add(trimmed);
      return true;
    }).join('\n');
  };

  // Process objections to ensure they display correctly
  const processObjections = (objections: string) => {
    if (!objections) return '';

    // Check if this is structured with Objection/Response format
    if (objections.includes('Objection:') || objections.includes('Objection 1:')) {
      // Format structured objections
      const formatted = objections
        .replace(/Objection\s*(\d+)?:\s*/g, (match, num) => 
          `\n**Objection ${num || ''}:** `)
        .replace(/Response\s*(\d+)?:\s*/g, (match, num) => 
          `\n**Response:** `)
        .replace(/^\n/, ''); // Remove leading newline if present
      
      return deduplicateText(formatted);
    }
    
    // If not structured, just deduplicate the text
    return deduplicateText(objections);
  };

  return (
    <div className="space-y-4">
      <div>
        <h3 className="font-medium">Question:</h3>
        <p>{item.content.question || 'N/A'}</p>
      </div>
      {item.content.response && (
        <div className="space-y-4">
          <h3 className="font-medium">Response:</h3>
          
          {hasContent(item.content.response.directAnswer) && (
            <div>
              <h4 className="text-sm font-medium text-heaven-gold">Direct Answer:</h4>
              <p className="whitespace-pre-line">{item.content.response.directAnswer}</p>
            </div>
          )}
          
          {hasContent(item.content.response.biblicalFoundation) && (
            <div>
              <h4 className="text-sm font-medium text-heaven-gold">Biblical Foundation:</h4>
              <p className="whitespace-pre-line">{item.content.response.biblicalFoundation}</p>
            </div>
          )}
          
          {item.content.response.scriptureReferences && item.content.response.scriptureReferences.length > 0 && (
            <div>
              <h4 className="text-sm font-medium text-heaven-gold">Scripture References:</h4>
              {item.content.response.scriptureReferences.map((scripture: any, i: number) => (
                <Card key={i} className="border-heaven-gold/10 mb-2">
                  <CardHeader className="py-2">
                    <CardTitle className="text-sm">{scripture.reference || `Scripture ${i+1}`}</CardTitle>
                    <CardDescription className="mt-1 italic">"{scripture.text || 'No text available'}"</CardDescription>
                    {scripture.explanation && (
                      <p className="text-xs mt-1">{deduplicateText(scripture.explanation)}</p>
                    )}
                  </CardHeader>
                </Card>
              ))}
            </div>
          )}
          
          {hasContent(item.content.response.theologicalReasoning) && (
            <div>
              <h4 className="text-sm font-medium text-heaven-gold">Theological Reasoning:</h4>
              <p className="whitespace-pre-line">{item.content.response.theologicalReasoning}</p>
            </div>
          )}
          
          {hasContent(item.content.response.historicalContext) && (
            <div>
              <h4 className="text-sm font-medium text-heaven-gold">Historical Context:</h4>
              <p className="whitespace-pre-line">{item.content.response.historicalContext}</p>
            </div>
          )}
          
          {hasContent(item.content.response.objections) && (
            <div>
              <h4 className="text-sm font-medium text-heaven-gold">Addressing Common Objections:</h4>
              <p className="whitespace-pre-line">{processObjections(item.content.response.objections)}</p>
            </div>
          )}
          
          {hasContent(item.content.response.finalEncouragement) && (
            <div>
              <h4 className="text-sm font-medium text-heaven-gold">Final Encouragement:</h4>
              <p className="whitespace-pre-line">{item.content.response.finalEncouragement}</p>
            </div>
          )}
        </div>
      )}
      
      <div className="flex flex-col sm:flex-row space-y-2 sm:space-y-0 sm:space-x-2 mt-4">
        <Button
          onClick={() => navigate(`/apologetics#atheism`)}
          className="flex-1"
        >
          Go to Atheism Defense Tool
        </Button>
        
        {item.content.response && (
          <ResponseActions
            question={item.content.question}
            response={item.content.response}
            toolType="atheism_defense"
          />
        )}
      </div>
    </div>
  );
};
